import { Grid } from '@mui/material';
import { Statistic } from '@tassat/common-definitions';
import { StatisticListGroup } from '@tassat/statistic';
import React from 'react';

import { printAssetStatistic, printNumberStatistic } from 'Lib/stringHelpers';
import { useAppSelector } from 'Reducers/store/hooks';
import { getFirmByFirmId, getFirmStatsObj } from 'Selectors/firms';
import { getFirmAssetStatsFromFirmId } from 'Selectors/firmStats';

import FirmDetailsPanel from './firmDetailsPanel';

import type { IFirmDetailsProps } from './';

export function FirmDetails(props: IFirmDetailsProps) {
    const {
        classes,
        firmId,
        assetPair
    } = props;

    const firm = useAppSelector((state) => getFirmByFirmId(state, firmId));
    const firmStats = useAppSelector((state) => getFirmStatsObj(state, firmId));
    const firmAssetStats = useAppSelector((state) => getFirmAssetStatsFromFirmId(state, firmId));
    if (!firm) return <div />;

    const transactionStatistics = () => {
        const statMap = [] as Array<{ title: string; statistic: any; statisticCount: any }>;
        firmAssetStats.forEach((firmAssetStat) => {
            if (firmAssetStat.AssetId === assetPair?.currency?.AssetId) {
                statMap.push({
                    title: 'Today\'s USD Sends',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysSendAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysSendCount)
                });
                statMap.push({
                    title: 'Today\'s USD Receives',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysReceivesAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysReceivesCount)
                });
                statMap.push({
                    title: 'Today\'s Withdrawals',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysWithdrawalAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysWithdrawalCount)
                });
                statMap.push({
                    title: 'Today\'s Deposits',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysDepositAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysDepositCount)
                });
            }
            if (firmAssetStat.AssetId === assetPair?.security?.AssetId) {
                statMap.push({
                    title: 'Today\'s TFND Sends',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysSendAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysSendCount)
                });
                statMap.push({
                    title: 'Today\'s TFND Receives',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysReceivesAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysReceivesCount)
                });
                statMap.push({
                    title: 'Today\'s Buys',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysBuyAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysBuyCount)
                });
                statMap.push({
                    title: 'Today\'s Sells',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysSellAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysSellCount)
                });
                statMap.push({
                    title: 'Today\'s Subscriptions',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysSubscriptionsAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysSubscriptionsCount)
                });
                statMap.push({
                    title: 'Today\'s Redemptions',
                    statistic: printAssetStatistic(firmAssetStat && firmAssetStat.TodaysRedemptionsAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(firmAssetStat && firmAssetStat.TodaysRedemptionsCount)
                });
            }
        });

        return statMap;
    };

    const firmStatistics: Statistic[] = [
        {
            title: 'Accounts',
            statistic: printNumberStatistic(firmStats && firmStats.wallets),
            statisticCount: printNumberStatistic(firmStats && firmStats.lockedWallets),
            lockCount: true
        },
        {
            title: 'Active Users',
            statistic: printNumberStatistic(firmStats && firmStats.activeUsers),
            statisticCount: printNumberStatistic(firmStats && firmStats.lockedActiveUsers),
            lockCount: true
        },
        {
            title: 'API Users',
            statistic: printNumberStatistic(firmStats && firmStats.apiUsers),
            statisticCount: printNumberStatistic(firmStats && firmStats.lockedApiUsers),
            lockCount: true
        }
    ];

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            justifyContent='flex-start'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                className={classes.content}
                container
                direction='row'
                item
                justifyContent='space-evenly'
                spacing={0}
                xs={12}
            >
                <Grid
                    alignItems='center'
                    className={classes.statisticsSectionContainer}
                    container
                    direction='column'
                    item
                    justifyContent='space-around'
                    wrap='nowrap'
                >
                    <StatisticListGroup
                        id='firmDetails_transactionStatistics'
                        itemClass={classes.statContainer}
                        statistics={transactionStatistics()}
                    />
                </Grid>
                <Grid
                    className={classes.sectionContainer}
                    item
                    xs={6}
                >
                    <FirmDetailsPanel
                        firm={firm}
                        firmAssetStats={firmAssetStats}
                    />
                </Grid>
                <Grid
                    alignItems='center'
                    className={classes.statisticsSectionContainer}
                    container
                    direction='column'
                    item
                    justifyContent='space-around'
                    spacing={0}
                >
                    <StatisticListGroup
                        id='firmDetails_firmStatistics'
                        statistics={firmStatistics}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}